import { PageLocaleEnum } from 'common/enums/PageLocaleEnum'
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react'
import { getCaptcha } from 'src/public-pages/api/website-public-page-api'
import { appendBodyScript } from 'src/public-pages/utils/append-script'

interface RecaptchaProps {
  onTokenChange: (token: string) => void
  onError?: () => void
}

export interface RecaptchaHandleInterface {
  reset: () => void
}

const localeMapping: Record<string, string> = {
  ua: 'uk',
  dk: 'da',
  jp: 'ja',
}

const getGoogleLocale = (locale?: string) =>
  locale ? (localeMapping[locale] ?? locale) : 'en'

export const Recaptcha = forwardRef<RecaptchaHandleInterface, RecaptchaProps>(
  ({ onTokenChange, onError }, ref) => {
    const recaptchaRef = useRef<HTMLDivElement>(null)
    const widgetIdRef = useRef<number | null>(null)
    const [captchaKey, setCaptchaKey] = useState<string | null>(null)
    const locale =
      typeof window !== 'undefined'
        ? (window.location.pathname.split('/').filter(Boolean)[0] ??
          PageLocaleEnum.ENGLISH)
        : PageLocaleEnum.ENGLISH

    useEffect(() => {
      getCaptcha()
        .then(res => setCaptchaKey(res.data?.openKey ?? ''))
        .catch(err => {
          if (err?.response?.status === 404) {
            setCaptchaKey('')
          }
        })
    }, [])

    const reset = useCallback(() => {
      onTokenChange('')
      if (widgetIdRef.current !== null) {
        window.grecaptcha?.reset(widgetIdRef.current)
      }
    }, [onTokenChange])

    useImperativeHandle(ref, () => ({ reset }), [reset])

    const loadRecaptcha = useCallback(async (captchaKey: string) => {
      const checkGrecaptcha = async () =>
        await new Promise((resolve, reject) => {
          const interval = setInterval(() => {
            if (typeof window.grecaptcha?.render === 'function') {
              clearInterval(interval)
              resolve(void 0)
            }
          }, 100)

          setTimeout(() => {
            clearInterval(interval)
            reject()
          }, 10000)
        })

      await checkGrecaptcha()

      const shouldRender =
        recaptchaRef.current &&
        !recaptchaRef.current.hasAttribute('data-rendered') &&
        typeof window.grecaptcha?.render === 'function'

      if (shouldRender) {
        widgetIdRef.current = window.grecaptcha!.render(recaptchaRef.current, {
          sitekey: captchaKey,
          callback: (token: string) => onTokenChange(token),
          size: 'normal',
          hl: getGoogleLocale(locale),
          'error-callback': onError,
          'expired-callback': reset,
        })
        recaptchaRef.current.setAttribute('data-rendered', 'true')
      }
    }, [])

    useEffect(() => {
      if (!captchaKey) return

      appendBodyScript(
        `https://www.google.com/recaptcha/api.js?render=explicit&hl=${getGoogleLocale(locale)}`,
      ).then(() => loadRecaptcha(captchaKey))
    }, [captchaKey])

    if (!captchaKey) {
      return null
    }

    return (
      <div ref={recaptchaRef} style={{ width: '100%', maxWidth: '28rem' }} />
    )
  },
)

Recaptcha.displayName = 'Recaptcha'
